import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  Button,
  Col,
  Form,
  Row
} from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import draggableico from "../../assets/images/draggable.svg";
import searchico from "../../assets/images/search-white.svg";
import swapleft from "../../assets/images/swapleft.svg";
import swapright from "../../assets/images/swapright.svg";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import ESAutocompleteSearch from "../../controls/ESAutocompleteSearch";
import {
  getAllQuestionCategory,
  getAllSurveySectionQuestions,
  insertSurveySectionsQuestions,
  surveygetAllQuestions,
  updateSurveySectionsQuestions,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import { showToast, ToastTypeCode, QUIZ_ALLOWED_RESPONSE_TYPES, RouteFlagConstant } from "../../utils/Constant";
import { Option, particularOrganization } from "../../utils/common";
import Loader from "../loader/Loader";
import "./AddSurveySectionQuestion.scss";

interface Question {
  QuestionId: number;
  QuestionTitle: string;
  SurveySectionId: number;
}


interface QuestionCategory {
  QuestionCategoryId: number;
  QuestionCategoryName: string;
}

interface AddSurveySectionQuestionProps {
  sectionId: number;
  onClose: () => void;
  editId: number | null;
  surveyFormId: any;
  organizationId: number | null | undefined;
  routeFlag: number;
  isDemography?: boolean;
}

interface SurveySectionQuestionEdit {
  SurveySectionQuestionId?: number;
  SurveyFormId: { label: string; value: number } | null;
  QuestionText: string;
  QuestionCategory: { label: string; value: number } | null;
}

const AddSurveySectionQuestion: React.FC<AddSurveySectionQuestionProps> = ({
  sectionId,
  onClose,
  editId,
  surveyFormId,
  organizationId,
  routeFlag,
  isDemography,
}) => {
  const [currentPage] = useState(1);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState(questions);
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);
  const [questionCategoryData, setQuestionCategoryData] = useState<Option[]>([]);
  const [checkedAvailableQuestions, setCheckedAvailableQuestions] = useState<
    Set<number>
  >(new Set());
  const [checkedSelectedQuestions, setCheckedSelectedQuestions] = useState<
    Set<number>
  >(new Set());
  const [pageSize] = useState(20);
  const [sortDir] = useState<string>("ASC");
  const [searchText, setSearchText] = useState<string>("");
  const [selectedQuestionCategory, setSelectedQuestionCategory] = useState<
    number | null
  >(null);
  const [sortBy, setSortBy] = useState<string>("SequenceNumber");
  const [loading, setLoading] = useState(false);
  const [selectAllCheckedLeftSide, setSelectAllCheckedLeftSide] =
    useState(false);
  const [selectAllCheckedRightSide, setSelectAllCheckedRightSide] =
    useState(false);

  const [questionData, setQuestionData] = useState<any>(null);

  const methods = useForm<SurveySectionQuestionEdit>({
    defaultValues: {
      SurveySectionQuestionId: 0,
      SurveyFormId: null,
      QuestionText: "",
      QuestionCategory: null,
    },
  });

  const fetchQuestionCategories = async (organizationId: number, isDemography: boolean) => {
    try {
      const response: any = await axiosPost(getAllQuestionCategory, {
        SearchText: "",
        IsActive: true,
        CurrentPageNo: 1,
        PageSize: 0,
        SortDir: "DESC",
        SortBy: "QuestionCategoryId",
        OrganizationId: organizationId,
        IsDemography: isDemography,
      });

      const data = response?.data?.ExtraData || [];
      return data?.map((item: QuestionCategory) => ({
        label: item?.QuestionCategoryName,
        value: item?.QuestionCategoryId,
      }));
    } catch (error) {
      showToast("Error fetching question categories", ToastTypeCode.ERROR);
      return [];
    }
  };

  const fetchDataForQuestionCategory = async () => {
    const data = await fetchQuestionCategories(organizationId ?? -1, isDemography ?? false);
    setQuestionCategoryData(data);
  };

  useEffect(() => {
    fetchDataForQuestionCategory();
  }, [organizationId, isDemography]);

  const handleQuestionCategoryChange = (selectedOption: Option | null) => {
    if (!selectedOption) {
      setQuestions([]);
    }
    methods.setValue("QuestionCategory", selectedOption);
    setSelectedQuestionCategory(selectedOption?.value || null);
  };

  const fetchAllSurveySectionQuestions = async () => {
    if (editId) {
      try {
        const response: any = await axiosPost(getAllSurveySectionQuestions, {
          SearchText: searchText,
          IsActive: true,
          CurrentPageNo: currentPage,
          PageSize: pageSize,
          SortDir: sortDir,
          SortBy: sortBy,
          SurveySectionId: editId,
        });
        const data = response.data.ExtraData;
        setQuestionData(data);

        const selected = data?.map((item: any) => ({
          QuestionId: item?.QuestionId,
          QuestionTitle: item?.QuestionTitle,
          SurveySectionId: item?.SurveySectionId,
        }));
        setSelectedQuestions(selected);

      } catch (error) {
        showToast(
          "Error fetching Form section question data",
          ToastTypeCode?.ERROR
        );
      }
    }
  };

  useEffect(() => {
    fetchAllSurveySectionQuestions();
  }, [editId, sectionId]);

  const fetchQuestions = async () => {
    if (selectedQuestionCategory) {
      setLoading(true);
      try {
        const response: any = await axiosPost(surveygetAllQuestions, {
          SurveyFormId: surveyFormId,
          SortBy: "CreateDate",
          CurrentPageNo: 1,
          PageSize: 0,
          SortDir: "DESC",
          IsActive: true,
          SearchText: searchText,
          QuestionCategoryId: selectedQuestionCategory,
          OrganizationId: organizationId ? organizationId : particularOrganization,
          routeFlag: routeFlag,
          IsDemography: isDemography ?? false,
        });

        if (response?.data?.Status === 200) {
          const fetchedQuestions = response.data.ExtraData;

          let filteredQuestions = fetchedQuestions?.filter(
            (fetchedQuestion: any) =>
              !selectedQuestions?.some(
                (selectedQuestion: any) =>
                  selectedQuestion?.QuestionId === fetchedQuestion?.QuestionId
              )
          );

          // For Quiz, only show questions with allowed response types
          if (routeFlag === RouteFlagConstant.QUIZ) {
            filteredQuestions = filteredQuestions?.filter(
              (q: any) =>
                !q.ResponseTypeName ||
                QUIZ_ALLOWED_RESPONSE_TYPES.includes(q.ResponseTypeName)
            );
          }

          setQuestions(filteredQuestions);
        } else {
          showToast("Failed to fetch questions", ToastTypeCode?.ERROR);
        }
      } catch (error) {
        showToast(
          "Something went wrong while fetching questions",
          ToastTypeCode?.ERROR
        );
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [selectedQuestionCategory]);

  useEffect(() => {
    setFilteredQuestions(questions);
  }, [questions]);

  const handleQuestionSelect = (item: any) => {
    if (selectedQuestions.some((q) => q.QuestionId === item.QuestionId)) {
      showToast("Question is already selected", ToastTypeCode.WARNING);
      return;
    }

    const questionData = {
      QuestionId: item.QuestionId,
      QuestionTitle: item.QuestionTitle,
      SurveySectionId: sectionId,
    };

    setSelectedQuestions((prev) => [...prev, questionData]);
    setQuestions((prev) => prev.filter((q) => q.QuestionId !== item.QuestionId));
    setFilteredQuestions((prev) =>
      prev.filter((q) => q.QuestionId !== item.QuestionId)
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchText(newValue);
    handleSearch(newValue);
  };

  const handleSearch = (searchText: string) => {
    const text = searchText;
    setSearchText(text);

    if (searchText.trim() === "") {
      setFilteredQuestions(questions);
    } else {
      const filtered = questions.filter((question) =>
        question.QuestionTitle.toLowerCase().includes(text)
      );
      setFilteredQuestions(filtered);
    }
  };

  const handleDragEnd = (result: any) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === "available") {
        const items = Array.from(questions);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setQuestions(items);
      } else if (source.droppableId === "selected") {
        const items = Array.from(selectedQuestions);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setSelectedQuestions(items);
      }
      return;
    }

    const draggedId = parseInt(draggableId);
    const isFromAvailable = source.droppableId === "available";
    const sourceQuestions = isFromAvailable ? Array.from(questions) : Array.from(selectedQuestions);
    const destinationQuestions = isFromAvailable ? Array.from(selectedQuestions) : Array.from(questions);
    const checkedSet = isFromAvailable ? checkedAvailableQuestions : checkedSelectedQuestions;

    let itemsToMove: Question[] = [];

    // If dragging an item that is part of the checked selection, move the whole selection
    if (checkedSet.has(draggedId)) {
      itemsToMove = sourceQuestions.filter(q => checkedSet.has(q.QuestionId));
    } else {
      // Otherwise, move only the single dragged item
      const item = sourceQuestions.find(q => q.QuestionId === draggedId);
      if (item) itemsToMove = [item];
    }

    if (itemsToMove.length === 0) return;

    // Filter out moved items from source and add to destination at specific index
    const updatedSource = sourceQuestions.filter(q => !itemsToMove.some(m => m.QuestionId === q.QuestionId));
    const updatedDestination = [...destinationQuestions];
    updatedDestination.splice(destination.index, 0, ...itemsToMove);

    if (isFromAvailable) {
      setQuestions(updatedSource);
      setSelectedQuestions(updatedDestination);
      // Clear moved items from available checked set
      setCheckedAvailableQuestions(prev => {
        const next = new Set(prev);
        itemsToMove.forEach(m => next.delete(m.QuestionId));
        return next;
      });
    } else {
      setSelectedQuestions(updatedSource);
      setQuestions(updatedDestination);
      // Clear moved items from selected checked set
      setCheckedSelectedQuestions(prev => {
        const next = new Set(prev);
        itemsToMove.forEach(m => next.delete(m.QuestionId));
        return next;
      });
    }
  };

  const moveSelectedToAvailable = () => {
    const selectedIds = Array.from(checkedSelectedQuestions);
    const questionsToMove = selectedQuestions.filter((q) =>
      selectedIds.includes(q.QuestionId)
    );
    setQuestions((prevQuestions) => [...prevQuestions, ...questionsToMove]);
    setSelectedQuestions((prevSelected) =>
      prevSelected.filter((q) => !selectedIds.includes(q.QuestionId))
    );
    setCheckedSelectedQuestions(new Set());
    setSelectAllCheckedRightSide(false);
  };

  const moveSelectedToChosen = () => {
    const selectedIds = Array.from(checkedAvailableQuestions);
    const questionsToMove = questions.filter((q) =>
      selectedIds.includes(q.QuestionId)
    );
    setSelectedQuestions((prevSelected) => [
      ...prevSelected,
      ...questionsToMove,
    ]);
    setQuestions((prevQuestions) =>
      prevQuestions.filter((q) => !selectedIds.includes(q.QuestionId))
    );
    setCheckedAvailableQuestions(new Set());
    setSelectAllCheckedLeftSide(false);
  };

  const handleSelectAllAvailable = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setCheckedAvailableQuestions((prev) => {
      const newSet = new Set<number>();
      if (isChecked) {
        questions.forEach((q) => newSet.add(q.QuestionId));
      }
      return newSet;
    });
  };

  const handleSelectAllSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setCheckedSelectedQuestions((prev) => {
      const newSet = new Set<number>();
      if (isChecked) {
        selectedQuestions.forEach((q) => newSet.add(q.QuestionId));
      }
      return newSet;
    });
  };

  const handleCheckboxChange = (
    questionId: number,
    isSelected: boolean,
    targetList: "available" | "selected"
  ) => {
    if (targetList === "available") {
      setCheckedAvailableQuestions((prev) => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(questionId);
        } else {
          newSet.delete(questionId);
        }
        return newSet;
      });
    } else {
      setCheckedSelectedQuestions((prev) => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(questionId);
        } else {
          newSet.delete(questionId);
        }
        return newSet;
      });
    }
  };

  const onSubmit: SubmitHandler<SurveySectionQuestionEdit> = async (data) => {
    if (selectedQuestions?.length === 0) {
      showToast("Please select at least one question", ToastTypeCode?.ERROR);
      return;
    }
    setLoading(true);
    try {
      const response: any = await axiosPost(
        editId ? updateSurveySectionsQuestions : insertSurveySectionsQuestions,
        {
          surveySectionId: sectionId,
          QuestionIds: selectedQuestions?.map(
            (question) => question.QuestionId
          ),
          OrganizationId: organizationId ? organizationId : 0,
          SurveyFormId: surveyFormId,
        }
      );
      if (response?.data?.Status === 200 && !response?.data?.ErrorMessage) {
        showToast(
          `Questions ${editId ? "updated" : "saved"} successfully`,
          ToastTypeCode?.SUCCESS
        );
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Something went wrong", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
    onClose();
  };



  return (
    <div className="survey-section">
      <div className="sub-card mt-2">
        {loading ? (
          <Loader />
        ) : (
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <Row>
                <div className="question-search d-flex align-items-center">
                  <div className="check-selectall">
                    <Form.Check
                      type="checkbox"
                      label="Select All"
                      name="AvailableSelectAll"
                      onChange={(e) => {
                        handleSelectAllAvailable(e);
                        setSelectAllCheckedLeftSide(e.target.checked);
                      }}
                      checked={selectAllCheckedLeftSide}
                    />
                  </div>

                  <Form.Group className="flex-fill" style={{ minWidth: 0 }}>
                    <ESSearchDropdown
                      options={questionCategoryData ?? []}
                      onChange={handleQuestionCategoryChange}
                      defaultValue={methods.getValues("QuestionCategory")}
                      placeholder="Question Categories"
                      name={"Question Categories"}
                      required={!editId}
                    />
                  </Form.Group>

                  <div className="flex-fill search-question" style={{ minWidth: 0 }}>
                    <ESAutocompleteSearch
                      apiUrl={surveygetAllQuestions}
                      labelKey="QuestionTitle"
                      valueKey="QuestionId"
                      initialValue={searchText}
                      onSearchChange={(text) => {
                        setSearchText(text);
                        handleSearch(text);
                      }}
                      onSelect={(item) => {
                        handleQuestionSelect(item);
                      }}
                      onSubmit={() => handleSearch(searchText)}
                      otherParams={{
                        OrganizationId: particularOrganization || organizationId,
                        IsActive: true,
                        QuestionCategoryId: selectedQuestionCategory,
                      }}
                    />
                  </div>
                  <div className="check-selectall">
                    <Form.Check
                      type="checkbox"
                      label="Select All"
                      name="SelectedSelectAll"
                      onChange={(e) => {
                        handleSelectAllSelected(e);
                        setSelectAllCheckedRightSide(e.target.checked);
                      }}
                      checked={selectAllCheckedRightSide}
                    />
                  </div>
                </div>
              </Row>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Row>   <Col
                  md={12}
                  className="d-flex align-items-center justify-content-center mt-2"
                >
                  <div>
                    <Button
                      onClick={moveSelectedToChosen}
                      className="mb-2 btn-swap"
                    >
                      <img src={swapright} alt="Swap right" />
                    </Button>
                    <Button
                      onClick={moveSelectedToAvailable}
                      className="mb-2 btn-swap"
                    >
                      <img src={swapleft} alt="Swap left" />
                    </Button>
                  </div>
                </Col></Row>
                <Row>
                  <Col md={6}>
                    {/* <h5>Available Questions</h5> */}
                    <div className="available-questions my-3">
                      <Droppable droppableId="available">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="user-list"
                          >
                            {filteredQuestions?.map((question, index) => (
                              <Draggable
                                key={question.QuestionId}
                                draggableId={`${question.QuestionId}`}
                                index={index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="question-draggable"
                                  >
                                    <Form.Check
                                      type="checkbox"
                                      label={
                                        <span title={question.QuestionTitle}>
                                          {question.QuestionTitle?.length > 30
                                            ? `${question.QuestionTitle.substring(
                                              0,
                                              30
                                            )}...`
                                            : question.QuestionTitle}
                                        </span>
                                      }
                                      checked={checkedAvailableQuestions.has(
                                        question.QuestionId
                                      )}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          question.QuestionId,
                                          e.target.checked,
                                          "available"
                                        )
                                      }
                                    />
                                    <span className="justify-content-end">
                                      <img src={draggableico} alt="drag" />
                                    </span>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  </Col>

                  <Col md={6}>
                    {/* <h5>Selected Questions</h5> */}
                    <div className="selected-questions my-3">
                      <Droppable droppableId="selected" >
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="user-list"
                          >
                            {selectedQuestions?.map((question, index) => (
                              <Draggable
                                key={question.QuestionId}
                                draggableId={`${question.QuestionId}`}
                                index={index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="question-draggable"
                                  >
                                    <Form.Check
                                      type="checkbox"
                                      label={
                                        <span title={question.QuestionTitle}>
                                          {question.QuestionTitle?.length > 30
                                            ? `${question.QuestionTitle.substring(
                                              0,
                                              30
                                            )}...`
                                            : question.QuestionTitle}
                                        </span>
                                      }
                                      checked={checkedSelectedQuestions.has(
                                        question.QuestionId
                                      )}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          question.QuestionId,
                                          e.target.checked,
                                          "selected"
                                        )
                                      }
                                    />
                                    <span>
                                      <img src={draggableico} alt="drag" />
                                    </span>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  </Col>
                </Row>

              </DragDropContext>
              <Button type="submit" className="mb-2 save-btn">
                {editId ? "Update Questions" : "Save Questions"}
              </Button>
              &nbsp;
              <Button
                variant="secondary"
                onClick={onClose}
                className="back-btn"
              >
                Cancel
              </Button>
            </Form>
          </FormProvider>
        )}
      </div>
    </div>
  );
};

export default AddSurveySectionQuestion;
